import { defineStore } from "pinia"
import {
  GrafanaMetricId,
  KitChartType,
  GrafanaMetricData,
  KitMetricPeriod,
  KitMetricId,
  EvercamStats,
} from "@evercam/shared/types"
import { AdminApi, EvercamLabsApi } from "@evercam/shared/api"
import { useKitSummaryStore } from "@/stores/kitSummary"
import { useNuxtApp } from "#app"

type MetricsByChartType = Record<
  KitChartType,
  Array<Record<GrafanaMetricId, GrafanaMetricData>>
>

type KitMetricsState = {
  selectedMetricsPeriod: KitMetricPeriod
  metricsByChartType: MetricsByChartType
  kitSolarChargerStats: EvercamStats[]
  kitPanelPowerStats: EvercamStats[]
  isFetchingMetrics: boolean
}

export const useKitMetricsStore = defineStore({
  id: "kitMetrics",
  state: (): KitMetricsState => ({
    metricsByChartType: {} as MetricsByChartType,
    selectedMetricsPeriod: KitMetricPeriod.Last1Hour,
    kitSolarChargerStats: [] as EvercamStats[],
    kitPanelPowerStats: [] as EvercamStats[],
    isFetchingMetrics: false,
  }),
  actions: {
    async fetchMetrics() {
      try {
        this.isFetchingMetrics = true
        await this.fetchKitBatteryAndPanelStats()
        await this.fetchGrafanaMetrics()
      } catch (e) {
        console.log("Failed to fetch metrics", e)
      } finally {
        this.isFetchingMetrics = false
      }
    },
    async fetchKitBatteryAndPanelStats() {
      try {
        const kitSummaryStore = useKitSummaryStore()
        const stats = await AdminApi.kits.getKitStats({
          kitId: kitSummaryStore.kit.id,
          metric: KitMetricId.SolarCharger,
          fromDate: this.metricsPeriodFromDate,
          toDate: this.metricsPeriodToDate,
        })
        const { solarChargerStats, panelPowerStats } = stats.reduce(
          (acc, entry) => {
            const timestamp = entry.timestamp
            acc.solarChargerStats.push({
              timestamp,
              voltage: entry.voltage / 1000,
              current: entry.current,
            })
            acc.panelPowerStats.push({
              timestamp,
              panelVoltage: entry.panelVoltage / 1000,
              panelPower: entry.panelPower,
            })

            return acc
          },
          { solarChargerStats: [], panelPowerStats: [] }
        )

        this.kitSolarChargerStats = solarChargerStats
        this.kitPanelPowerStats = panelPowerStats
      } catch (e) {
        console.error("Failed to fetch kit solar charger stats", e)
      }
    },
    async fetchGrafanaMetrics() {
      const metrics = await EvercamLabsApi.kits.getMetrics(
        useKitSummaryStore().kit.serial,
        [
          GrafanaMetricId.LocalStorage,
          GrafanaMetricId.RouterCpuLoad,
          GrafanaMetricId.SbcRamUsed,
          GrafanaMetricId.SbcRootFsUsed,
          GrafanaMetricId.RouterTemperature,
          GrafanaMetricId.SbcCpuStats,
          GrafanaMetricId.SbcRamStats,
          GrafanaMetricId.SbcTemperatureStats,
          GrafanaMetricId.StorageSpaceUsed,
          GrafanaMetricId.SbcNetworkStats,
          GrafanaMetricId.RouterSINR,
        ],
        this.selectedMetricsPeriod
      )

      this.metricsByChartType = Object.entries(metrics).reduce(
        (acc, [metricName, metric]) => {
          if (!metric?.type) {
            return acc
          }

          return {
            ...acc,
            [metric.type]: {
              ...(acc[metric.type] || {}),
              [metricName]: metric,
            },
          }
        },
        {} as MetricsByChartType
      )
    },
  },
  getters: {
    metricsPeriodFromDate(): string {
      const matches = this.selectedMetricsPeriod.match(/now-(\d+)([mhd])/)
      const value = parseInt(matches[1] || "1")
      const unit = matches[2]

      let timeUnit
      switch (unit) {
        case "m":
          timeUnit = "minutes"
          break
        case "h":
          timeUnit = "hours"
          break
        case "d":
          timeUnit = "days"
          break
        default:
          timeUnit = "hours"
      }

      return useNuxtApp()
        .nuxt2Context.$moment(new Date())
        .subtract(value, timeUnit)
        .toISOString()
    },
    metricsPeriodToDate(): string {
      return useNuxtApp().nuxt2Context.$moment(new Date()).toISOString()
    },
  },
})
